import { FaCheck, FaLock } from 'react-icons/fa6';

/**
 * Add-on entry supporting backend property variations (`images` or `image`)
 */
export interface AddOnItem {
  additionalServiceId?: string;
  id?: string;
  serviceItem?: string;
  serviceTitle?: string;
  title?: string;
  price?: number;
  image?: string;
  images?: string | string[];
  description?: string;
}

export const getAddOnId = (addon: AddOnItem, index: number): string =>
  addon.additionalServiceId || addon.id || `addon_${index}`;

export const getAddOnName = (addon: AddOnItem, index: number): string =>
  addon.serviceItem ||
  addon.serviceTitle ||
  addon.title ||
  `Add-on ${index + 1}`;

/**
 * Extract image URL from either string or array response shape
 */
export const getAddOnImage = (addon: AddOnItem): string | undefined => {
  if (addon.image) return addon.image;
  if (typeof addon.images === 'string') return addon.images;
  if (Array.isArray(addon.images) && addon.images.length > 0)
    return addon.images[0];
  return undefined;
};

interface AddOnSelectorProps {
  addons: AddOnItem[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /**
   * Phase 3.1 — renders every addon in this list as a locked, non-toggleable
   * "Included" card instead of a selectable one. Used for additional
   * services a customer already paid for as part of a package (see
   * CreditSession.tsx, which renders package-included add-ons in one
   * read-only AddOnSelector and the remaining, still-purchasable extras in a
   * second, normal one) — there is nothing to "select" here, so clicking
   * does nothing and `onChange` is never called.
   */
  readOnly?: boolean;
}

/**
 * Modern Add-on Selector matching horizontal card UI
 */
const AddOnSelector = ({
  addons,
  selectedIds,
  onChange,
  readOnly = false,
}: AddOnSelectorProps) => {
  const toggle = (id: string) => {
    if (readOnly) return;
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  if (!addons || addons.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
      {addons.map((addon, index) => {
        const id = getAddOnId(addon, index);
        const isSelected = readOnly ? true : selectedIds.includes(id);
        const name = getAddOnName(addon, index);
        const imageUrl = getAddOnImage(addon);
        // Formatting to match the 'CAD 20,000' style (no decimals, comma separator)
        const price = Number(addon.price || 0).toLocaleString('en-CA', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });

        return (
          <button
            key={id}
            onClick={() => toggle(id)}
            type="button"
            disabled={readOnly}
            aria-disabled={readOnly}
            className={`relative flex items-center p-2 text-left rounded-2xl border transition-all duration-200 w-full ${
              readOnly ? 'cursor-default' : 'cursor-pointer'
            } ${
              readOnly
                ? 'border-success ring-succes bg-success-50/40'
                : isSelected
                  ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50/20 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
            }`}
          >
            {/* Image/Icon Container */}
            <div className="relative shrink-0 w-14 h-14 rounded-xl bg-[#1e293b] overflow-hidden flex items-center justify-center">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              ) : (
                /* Fallback icon if no image is provided */
                <span className="text-white/80 font-bold text-[10px] tracking-widest">
                  LOGO
                </span>
              )}
            </div>

            {/* Content */}
            <div className="ml-4 flex flex-col justify-center">
              <h4 className="font-semibold text-[15px] text-slate-800">
                {name}
              </h4>
              {readOnly ? (
                <div className="text-[13px] font-semibold text-success ">
                  Included · no extra charge
                </div>
              ) : (
                <div className="text-[15px] font-semibold text-secondary mt-0.5">
                  CAD {price}
                </div>
              )}
            </div>

            {/* Selected/Included indicator */}
            {readOnly ? (
              <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-success-500 text-white text-[10px] font-semibold">
                <FaLock size={8} />
                Included
              </div>
            ) : (
              isSelected && (
                <div className="absolute top-2 right-2 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <FaCheck size={8} className="text-white" />
                </div>
              )
            )}
          </button>
        );
      })}
    </div>
  );
};

export default AddOnSelector;
